import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add,
  Delete,
  Dashboard as DashboardIcon,
  Visibility,
} from '@mui/icons-material';
import APIKit from '../../../config/APIKit';
import { showSnackBar } from '../../store/compatibility';
import { useDispatch } from 'react-redux';
import style from '../settings/settings.module.scss';
import Title from '../settings/Title';
import ConfirmationDialog from '../../component/ConfirmationDialog';

export default function DashboardList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, dashboard: null });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: null,
    loading: false,
  });

  useEffect(() => {
    loadDashboards();
  }, []);

  const loadDashboards = async () => {
    try {
      setLoading(true);
      const response = await APIKit.get('/dashboards');
      if (response.success) {
        setDashboards(response.data || []);
      }
    } catch (error) {
      console.error('Error loading dashboards:', error);
      dispatch(showSnackBar({ message: 'Error loading dashboards', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModal({ open: true, dashboard: null });
  };

  const openConfirmDialog = (dashboardId) => {
    setConfirmDialog({
      open: true,
      id: dashboardId,
      loading: false,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      id: null,
      loading: false,
    });
  };

  const handleDelete = async () => {
    if (!confirmDialog.id) return;
    
    setConfirmDialog(prev => ({
      ...prev,
      loading: true,
    }));

    try {
      await APIKit.delete(`/dashboards/${confirmDialog.id}`);
      dispatch(showSnackBar({ message: 'Dashboard deleted successfully', severity: 'success' }));
      loadDashboards();
      closeConfirmDialog();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error deleting dashboard', severity: 'error' }));
      setConfirmDialog(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  return (
    <div className={style.container}>
      <Title
        title="Dashboards"
        handleModal={handleCreate}
        enableButton={true}
      />

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {dashboards.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 167, 142, 0.1)',
                position: 'relative',
                p: 4,
                textAlign: 'center',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #00a78e 0%, #0f766e 50%, #f093fb 100%)',
                  zIndex: 1,
                },
              }}
            >
              <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                No dashboards yet. Create your first dashboard to get started.
              </Typography>
            </Box>
          </Grid>
        ) : (
          dashboards.map(dashboard => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={dashboard.id}>
              <Card
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid rgba(0, 167, 142, 0.1)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 6px 20px rgba(0, 0, 0, 0.1)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #00a78e 0%, #0f766e 50%, #f093fb 100%)',
                    zIndex: 1,
                  },
                }}
              >
                <CardContent sx={{ p: 3, pb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                          p: 1.5,
                          borderRadius: '12px',
                          color: '#00a78e',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <DashboardIcon />
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                          }}
                        >
                          {dashboard.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '13px', mt: 0.5 }}>
                          {dashboard.description || 'No description'}
                        </Typography>
                      </Box>
                    </Box>
                    {dashboard.is_default && (
                      <Chip
                        label="Default"
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                          color: '#ffffff',
                          fontWeight: 600,
                          borderWidth: '2px',
                        }}
                      />
                    )}
                  </Box>
                  <Chip
                    label={dashboard.category}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(0, 167, 142, 0.3)',
                      color: '#00a78e',
                      fontWeight: 600,
                    }}
                  />
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => navigate(`/analytics/dashboards/${dashboard.id}`)}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                      color: '#00a78e',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                        color: '#ffffff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 167, 142, 0.3)',
                      },
                    }}
                  >
                    View
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => openConfirmDialog(dashboard.id)}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(239, 83, 80, 0.1) 0%, rgba(229, 57, 53, 0.1) 100%)',
                      color: '#ef5350',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                        color: '#ffffff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(239, 83, 80, 0.3)',
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={handleDelete}
        title="Delete Dashboard?"
        message="Are you sure you want to delete this dashboard? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
        confirmColor="error"
        loading={confirmDialog.loading}
      />

      <CreateDashboardModal
        open={modal.open}
        onClose={() => setModal({ open: false, dashboard: null })}
        onSave={loadDashboards}
      />
    </div>
  );
}

function CreateDashboardModal({ open, onClose, onSave }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom',
    is_default: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await APIKit.post('/dashboards', formData);
      if (response.success) {
        dispatch(showSnackBar({ message: 'Dashboard created successfully', severity: 'success' }));
        onSave();
        onClose();
        navigate(`/analytics/dashboards/${response.data.id}`);
      }
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error creating dashboard', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '20px',
          py: 2.5,
          px: 3,
          mb: 0,
        }}
      >
        Create Dashboard
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 3, overflow: 'visible' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00a78e',
                },
              }}
            />
          </Box>
          <Box>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00a78e',
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 0.5, mt: 0.5 }}>
            <FormControl fullWidth>
              <InputLabel
                id="category-label"
                sx={{
                  '&.MuiInputLabel-root': {
                    backgroundColor: 'transparent',
                    px: 0.5,
                    transform: 'translate(14px, 16px) scale(1)',
                  },
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                }}
              >
                Category
              </InputLabel>
              <Select
                labelId="category-label"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Category"
                sx={{
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '12px',
                  },
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                }}
              >
                <MenuItem value="executive">Executive</MenuItem>
                <MenuItem value="sales">Sales</MenuItem>
                <MenuItem value="finance">Finance</MenuItem>
                <MenuItem value="operations">Operations</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.4)',
              background: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
            boxShadow: '0 4px 16px rgba(0, 167, 142, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f766e 0%, #00a78e 100%)',
              boxShadow: '0 6px 20px rgba(0, 167, 142, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: 'rgba(0, 167, 142, 0.5)',
            },
          }}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
